// All related to survey job data services will be defined here
import { HttpClient, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom, map, Observable } from 'rxjs';
import { GspLoggerService } from 'src/app/log-handler.service';
import { ProjectStreamService } from 'src/app/shared/common/current-project/project-stream.service';
import { EnvironmentService } from 'src/app/shared/common/environment/environment.service';
import {
    SurveyJob,
    SurveyJobAttachment,
    SurveyJobCreateData,
    SurveyJobListOptions,
    SurveyJobPatchData
} from 'src/app/shared/survey-data-services/survey-job';

@Injectable({
    providedIn: 'root'
})
export class SurveyJobService {
    private surveyJobApiUrl: string;

    constructor(
        private http: HttpClient,
        private environmentService: EnvironmentService,
        private projectStream: ProjectStreamService,
        private logger: GspLoggerService
    ) {}

    // Always get current project survey jobs
    private prepareSurveyJobApiUrl(): void {
        const currentProjectId = this.projectStream.currentProject?.id || '';

        this.surveyJobApiUrl = `${this.environmentService.apiUrl}/projects/${currentProjectId}/surveyjobs`;
    }

    // Get all survey jobs
    // see more docs on SurveyJobListOptions
    getSurveyJobs(
        options?: Partial<SurveyJobListOptions>
    ): Observable<{ items: SurveyJob[]; total: number; statusCode?: HttpStatusCode }> {
        this.prepareSurveyJobApiUrl();
        const url = `${this.surveyJobApiUrl}/_query`;
        const params = new URLSearchParams();

        if (options?.filter) {
            params.append('filter', options.filter);
        }
        params.append('sortBy', options?.sortBy ?? 'UpdatedUtc DESC');

        if (options?.fields) {
            params.append('fields', options.fields);
        }

        if (options?.pagination) {
            params.append('pageIndex', String(options.pagination.page));
            params.append('pageSize', String(options.pagination.pageSize));
        }

        const fullUrl = `${url}?${params.toString()}`;

        return this.http
            .get<{ items: SurveyJob[]; total?: number; statusCode?: HttpStatusCode; lastPage?: number }>(fullUrl, {
                observe: 'response'
            })
            .pipe(
                map(response => ({
                    items: response.body?.items ?? [],
                    total: response.body?.total ?? 0,
                    statusCode: response.status || HttpStatusCode.Ok
                }))
            );
    }

    // Get a survey job by ID
    getSurveyJobById(jobId: string, includeAttachments = false): Promise<SurveyJob> {
        this.prepareSurveyJobApiUrl();
        return lastValueFrom(
            this.http
                .get<SurveyJob>(`${this.surveyJobApiUrl}/${jobId}`, {
                    params: { includeAttachments: String(includeAttachments) }
                })
                .pipe(map(response => response))
        );
    }

    // Create a new survey job
    createSurveyJob(jobData: SurveyJobCreateData): Promise<SurveyJob> {
        this.prepareSurveyJobApiUrl();
        return lastValueFrom(this.http.post<SurveyJob>(this.surveyJobApiUrl, jobData).pipe(map(response => response)));
    }

    // Download job Package
    downloadJobPackage(jobId: string): Observable<any> {
        this.prepareSurveyJobApiUrl();

        return this.http.get(`${this.surveyJobApiUrl}/${jobId}/download`, { responseType: 'blob' });
    }

    getSurveyJobAttachments(
        jobId: string,
        includeDownloadUrl = false
    ): Promise<{ total: number; items: SurveyJobAttachment[] }> {
        this.prepareSurveyJobApiUrl();
        return lastValueFrom(
            this.http
                .get<{ total: number; items: SurveyJobAttachment[] }>(`${this.surveyJobApiUrl}/${jobId}/attachments`, {
                    params: { includeDownloadUrl: String(includeDownloadUrl) }
                })
                .pipe(map(response => ({ total: response.total, items: response.items })))
        );
    }

    getLinkedProjectData(
        jobId: string,
        includeDownloadUrl = false
    ): Promise<{ total: number; items: SurveyJobAttachment[] }> {
        this.prepareSurveyJobApiUrl();
        return lastValueFrom(
            this.http
                .get<{ total: number; items: SurveyJobAttachment[] }>(`${this.surveyJobApiUrl}/${jobId}/linkedfiles`, {
                    params: { includeDownloadUrl: String(includeDownloadUrl) }
                })
                .pipe(map(response => ({ total: response.total, items: response.items })))
        );
    }

    patchSurveyJob(jobId: string, eTag: string, jobData: SurveyJobPatchData): Promise<SurveyJob> {
        this.prepareSurveyJobApiUrl();
        const headers = { 'If-Match': `"${eTag}"` };

        return lastValueFrom(
            this.http
                .patch<SurveyJob>(`${this.surveyJobApiUrl}/${jobId}`, jobData, { headers })
                .pipe(map(response => response))
        );
    }

    /**
     * Trigger project-level FES reprocess for Survey workspace (TCMAPS-5028)
     * This should sync survey data at the project level when a Survey workspace is first opened
     * @param projectId The project ID
     * @throws Error if projectId is invalid or doesn't exist (403 Forbidden)
     */
    public async triggerProjectLevelSync(projectId: string): Promise<boolean> {
        try {
            this.prepareSurveyJobApiUrl();
            const url = `${this.surveyJobApiUrl}/_fesreprocess`;
            await lastValueFrom(this.http.post(url, null));
            return true;
        } catch (error) {
            this.logger.error(`[Survey Workspace] Error triggering FES reprocess for project: ${projectId}`);
            this.logger.error(error);
            return false;
        }
    }
}
